import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { ILoginSuccessData, IBalanceUpdatedData, ILogger } from '@sparkware/uc-sdk-core';
import { ITrackingInfo } from '../tracking/models/ITrackingInfo';
import { OptimizelyExperimentData } from 'page-context-data';
import { Utils } from '../utils';
import {
    IOptimizely,
    IOptimizelyAudience,
    IOptimizelyHelperFunction,
    IUCOptimizely,
} from './interfaces';
import { OptimizelyEvents } from './enums';
import DeferredObject from '../../../Modules/Utils/DeferredObject';
import { FeatureAvailabilityToken, WindowToken } from '../../injection-tokens';
import { Features } from '../../models/enums/general-enums';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';

@Service()
export class Optimizely implements IOptimizely {
    private ucOptimizely: IUCOptimizely;
    private _postLoginCompleted: DeferredObject<boolean>;
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private readonly _utils: Utils;
    private readonly _featureAvailability: IFeatureAvailability;
    static readonly OptimizelyEvents = OptimizelyEvents;
    readonly OptimizelyEvents = Optimizely.OptimizelyEvents;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('Optimizely');
        this._window = Container.get(WindowToken);
        this._utils = Container.get(Utils);
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._postLoginCompleted = new DeferredObject<boolean>();
        this._window.optimizely = this._window.optimizely || undefined;
        this._buildUcOptimizely();
        void this._activeStartupEvents();
        this._logger.debug('Optimizely initiated successfully');
    }

    /////////////////////////
    /** PUBLIC EVENTS AREA */
    /////////////////////////
    public getOptimizelyData = (): unknown => {
        return this.ucOptimizely;
    };

    public async publishLowBalanceUpdate(data: IBalanceUpdatedData): Promise<void> {
        await this._postLoginCompleted.promise;
        this._logger.debug(`[publishLowBalanceUpdate] start, data: ${JSON.stringify(data)}`);
        this._setRemoveOnLogout(this.OptimizelyEvents.UC_BALANCE_UPDATE);
        this._connectSDKWithOptimizely('LDP', this.OptimizelyEvents.UC_BALANCE_UPDATE, data);
    }

    public async onLogoutSuccess(): Promise<void> {
        const { events } = this.ucOptimizely;
        Object.keys(events).forEach((eventName) => {
            if (events[eventName].shouldRemoveOnLogout) {
                this._disableEventExecution(eventName);
                this._pushOptimizelyEvent(eventName);
            }
        });
    }

    public async onLoginSuccess(data: ILoginSuccessData): Promise<void> {
        this._postLoginCompleted.resolve(true);
        this._enableEventExecution(this.OptimizelyEvents.UC_BALANCE_UPDATE);
        this._updateUserAudience(data);
    }

    private _buildUcOptimizely(): void {
        this.ucOptimizely = {
            events: this._fillOptimizelyEventsObj(),
            helperFunctions: this._setHelperFunctions(),
            audiences: this._setDefaultAudiences(),
            pageEvents: {},
        };
    }

    /**
     *  initial optimizely startup events
     * @private
     */
    private async _activeStartupEvents(): Promise<void> {
        const bottomNavBarEnabled = await this._featureAvailability.FeatureIsEnabled(
            Features.MOBILE_WEB_BOTTOM_NAVBAR,
        );
        if (!bottomNavBarEnabled) {
            this._activeBottomBarNavigation();
        }
    }

    private _activeBottomBarNavigation(): void {
        this._enableEventExecution(this.OptimizelyEvents.UC_BOTTOM_NAVIGATION_BAR);
        this._connectSDKWithOptimizely(
            'BottomNavigationBar',
            this.OptimizelyEvents.UC_BOTTOM_NAVIGATION_BAR,
            {},
        );
    }

    private _connectSDKWithOptimizely(expName: string, eventName: string, data: any) {
        const { experiments } = this._window.pageContextManager.getOptimizelyData() || {};
        if (experiments) {
            const experimentLDP = experiments.find(
                (exp: OptimizelyExperimentData) => exp.name === expName,
            );
            if (experimentLDP) {
                this._addExperimentID(eventName, experimentLDP.id);
            }
        }
        // this should be the last action - publish the event
        this._pushOptimizelyEvent(eventName, data);
    }

    /**
     * this function will fill the possible events
     * @private
     */
    private _fillOptimizelyEventsObj(): Record<OptimizelyEvents, any> {
        const eventsObject: { [key in OptimizelyEvents]: {} } = {} as {
            [key in OptimizelyEvents]: {};
        };
        Object.values(OptimizelyEvents).forEach((value) => {
            eventsObject[value as OptimizelyEvents] = {};
        });
        return eventsObject;
    }

    private _setHelperFunctions(): IOptimizelyHelperFunction {
        return {
            generateCorrelationID: (): string => {
                return this._utils.generateCorrelationID();
            },
            publishToMB: (channel: string, topic: string, data: any): void => {
                this._window.uc.channels[channel].topics[topic].publish(
                    { publisher: 'optimizely' },
                    data,
                );
            },
            publishAnalytics: (experimentID: string, eventData: ITrackingInfo): void => {
                if (!this._window.optimizely) return;
                const { experimentName, variation } = this._window.optimizely
                    .get('state')
                    .getExperimentStates()[experimentID];
                this._logger.debug('optimizely variation:', variation.name);
                this._logger.debug('optimizely experimentName:', experimentName);
                this._window.uc.channels.tracking.topics.sendAnalytics.publish(
                    { publisher: 'optimizely' },
                    {
                        dataEvent: 'UC_GENERIC_EVENT_important',
                        ...eventData,
                    },
                );
            },
            stopEventExecution: (eventName: string): void => {
                this._disableEventExecution(eventName);
            },
        };
    }

    private _setDefaultAudiences(): IOptimizelyAudience {
        return {
            device: this._window.pageContextManager.getDeviceData(),
            localization: this._window.pageContextManager.getLocalizationData(),
            isNative: this._utils.findIfIsNative(),
        };
    }

    private toCamelCase(input: string): string {
        return input.replace(/_([a-z])/g, (_, match) => match.toUpperCase());
    }

    private _pushOptimizelyEvent(eventName: string, newEventData: Record<string, any> = {}) {
        const eventData = this._updateEventData(eventName, newEventData);
        const event = {
            type: 'event',
            eventName,
            tags: eventData,
        };
        if (!this._window.optimizely) return;
        this._window.optimizely.push(event);
        const timeout = setTimeout(() => {
            const customEvent = new CustomEvent(this.toCamelCase(eventName), {
                detail: eventData,
            });
            this._window.dispatchEvent(customEvent);
            clearTimeout(timeout);
        }, 0);
    }

    private _disableEventExecution(eventName: string): void {
        const eventsActiveStatus =
            JSON.parse(sessionStorage.getItem('optimizelyActiveEvents')) || {};
        eventsActiveStatus[eventName] = false;
        sessionStorage.setItem('optimizelyActiveEvents', JSON.stringify(eventsActiveStatus));
    }

    private _enableEventExecution(eventName: string): void {
        const eventsActiveStatus =
            JSON.parse(sessionStorage.getItem('optimizelyActiveEvents')) || {};
        eventsActiveStatus[eventName] = true;
        sessionStorage.setItem('optimizelyActiveEvents', JSON.stringify(eventsActiveStatus));
    }

    private _setRemoveOnLogout(eventName: string): void {
        this.ucOptimizely.events[eventName].shouldRemoveOnLogout = true;
    }

    private _addExperimentID(eventName: string, expID: string): void {
        this.ucOptimizely.events[eventName].experimentID = expID;
    }

    private _updateEventData(
        eventName: string,
        newEventData: Record<string, any>,
    ): Record<string, any> {
        const previousEventData = this.ucOptimizely.events[eventName];
        const data = {
            ...previousEventData,
            ...newEventData,
        };
        this.ucOptimizely.events[eventName] = data;
        return data;
    }

    private _updateUserAudience(userDataObj: Record<string, any> = {}): void {
        this._logger.debug(
            `[updateUserAudience] start, userDataObj: ${JSON.stringify(userDataObj)}`,
        );
        this.ucOptimizely.audiences = {
            ...this.ucOptimizely.audiences,
            ...userDataObj,
        };
    }
}
